import { useTheme } from 'next-themes';
import { Sun, Moon, Monitor } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const THEME_OPTIONS = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

/**
 * Compact theme toggle button - cycles through system/light/dark on click
 * Used in headers (Admin and Member portals)
 */
export const ThemeToggle = ({ className, variant = 'admin', onThemeChange, disabled = false }) => {
  const { theme, setTheme, resolvedTheme } = useTheme();

  // Cycle through: system → light → dark → system
  const handleToggle = () => {
    const cycle = { system: 'light', light: 'dark', dark: 'system' };
    const nextTheme = cycle[theme] || 'system';
    setTheme(nextTheme);
    onThemeChange?.(nextTheme);
  };

  // Get the appropriate icon based on current theme setting
  const iconMap = { system: Monitor, light: Sun, dark: Moon };
  const CurrentIcon = iconMap[theme] || (resolvedTheme === 'dark' ? Moon : Sun);

  const buttonStyles = {
    admin: 'h-8 w-8 text-stone-700 hover:text-stone-900 hover:bg-stone-200/50 dark:text-amber-100 dark:hover:text-white dark:hover:bg-white/10',
    member: 'h-8 w-8 text-[#C9A227] hover:bg-stone-100 dark:hover:bg-white/10',
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(buttonStyles[variant], className)}
      onClick={handleToggle}
      disabled={disabled}
      aria-label={`Theme: ${theme} (click to change)`}
    >
      <CurrentIcon className="h-4 w-4" />
    </Button>
  );
};

/**
 * Expanded theme toggle section for mobile navigation
 * Shows all three options as buttons in a row
 */
export const ThemeToggleExpanded = ({ className, onThemeChange, disabled = false }) => {
  const { theme, setTheme } = useTheme();

  const handleThemeChange = (nextTheme) => {
    setTheme(nextTheme);
    onThemeChange?.(nextTheme);
  };

  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-xs uppercase text-[#C9A227] font-semibold tracking-wider">
        Appearance
      </p>
      <div className="flex gap-2">
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;
          return (
            <button
              key={option.value}
              onClick={() => handleThemeChange(option.value)}
              disabled={disabled}
              className={cn(
                'flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all',
                isActive
                  ? 'bg-[#C9A227]/20 text-[#C9A227] ring-1 ring-[#C9A227]/50'
                  : 'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 active:bg-stone-200 dark:active:bg-white/10'
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs font-medium">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeToggle;
